/**
 * Layout for a single Persuasion Lab build.
 * Covers sections: Persuasion Logic (argument-builder), Brief Source (activate), Research, Story Design (stories).
 * The header Back link returns to the section-level dashboard.
 * This layout wraps Persuasion Lab as the primary narrative authoring lane.
 */

import { ROUTES } from '@/constants/routes'
import { CampaignPageHeader } from '@/modules/campaigns/components'
import { persuasionLabService } from '@/services/persuasion-lab'
import { FC, useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { SectionSubNavRouter } from './SectionSubNavRouter'

export const PersuasionLabBuildLayout: FC = () => {
    const { runId } = useParams<{ runId: string }>()
    const navigate = useNavigate()
    const location = useLocation()
    const [buildName, setBuildName] = useState<string>('')

    const backToSectionDashboard = (() => {
        const path = location.pathname
        if (path.includes('/argument-builder')) return ROUTES.PERSUASION_LAB_ARGUMENT_BUILDER
        if (path.includes('/research')) return ROUTES.PERSUASION_LAB_RESEARCH
        if (path.includes('/stories')) return ROUTES.PERSUASION_LAB_STORIES
        if (path.includes('/creative-context')) return ROUTES.PERSUASION_LAB_CREATIVE_CONTEXT
        return ROUTES.PERSUASION_LAB
    })()

    void buildName

    useEffect(() => {
        if (!runId) return
        let cancelled = false
        persuasionLabService
            .getRun(runId)
            .then((data: any) => {
                if (!cancelled) setBuildName(data?.name || `Build`)
            })
            .catch(() => {
                if (!cancelled) navigate(backToSectionDashboard, { replace: true })
            })
        return () => {
            cancelled = true
        }
        // backToSectionDashboard and navigate are intentionally omitted — they are stable
        // for the lifetime of this runId and including them causes an infinite fetch loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runId])

    const sectionLabel = (() => {
        const path = location.pathname
        if (path.includes('/argument-builder')) return 'Back to Situations'
        if (path.includes('/research')) return 'Back to Research'
        if (path.includes('/stories')) return 'Back to Story Design'
        if (path.includes('/creative-context')) return 'Back to Persuasion Lab'
        return 'Back to Persuasion Lab'
    })()

    return (
        <div className='min-h-screen bg-[#F5F5F7]'>
            <CampaignPageHeader
                title={buildName || 'Loading…'}
                subtitle='Construct — Build Your Message'
                onBack={() => navigate(backToSectionDashboard)}
                backLabel={sectionLabel}
                showContinue={false}
            />
            <SectionSubNavRouter />
            <main className='w-full min-w-0 max-w-[1440px] mx-auto py-6 sm:py-8 px-4 sm:px-6 overflow-x-hidden'>
                <Outlet />
            </main>
        </div>
    )
}
